import { useCallback, useState } from 'react';
import Link from 'next/link';

import { doRequest } from '@r1-frontend/do-request';
import { confirmEmail } from '@r1-frontend/api-domru/api-profile/v1/contacts/confirm-email';
import { contactsSendMailForConfirm } from '@r1-frontend/api-domru/api-profile/v1/contacts/send-mail-for-confirm';
import { OpenChatLink as OpenChat } from '@r1-frontend/entities/Chat';
import { NON_BREAKING_SPACE } from '@r1-frontend/entities/Layout/_deps/constants/symbols';

import { btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';
import PopupResult, { EResultPopupStatus } from '@r1-frontend/ui-react/components/popups/popupResult';

import * as ST from './styled';

interface IProps {
    email: string,
    errorMessage: string,
    showPopup: boolean,
    popupStep: EPopupSteps,
    onClosePopup: () => void,
    contactId: number,
    setErrorMessage: (message: string) => void,
    setPopupStep: (step: EPopupSteps) => void,
    emailConfirmedHandler: () => void,
}

interface ICodeError {
    status: boolean,
    message: string,
}

export enum EPopupSteps {
    success,
    error,
    addedUnconfirmedEmail,
}

export const Popup = ({
    email,
    errorMessage,
    showPopup,
    popupStep,
    onClosePopup,
    contactId,
    setErrorMessage,
    setPopupStep,
    emailConfirmedHandler,
}: IProps) => {
    const [code, setCode] = useState<string>('');
    const [codeError, setCodeError] = useState<ICodeError>({
        status: false,
        message: '',
    });
    const changeCode = useCallback((value: string) => {
        setCode(value);
    }, []);

    const sendCodeAgain = useCallback(async() => {
        const url = location.origin + location.pathname;
        const response = await doRequest.apiProfile<string>(
            contactsSendMailForConfirm({
                contact_id: contactId.toString(),
                url,
                show_code: '1',
            }),
        );

        if (!response.isSuccess) {
            setErrorMessage(response.error.message ? response.error.message : '');
            setPopupStep(EPopupSteps.error);
        }
    }, [contactId]);

    const closePopupHandler = useCallback(() => {
        setCodeError({
            status: false,
            message: '',
        });
        setCode('');
        onClosePopup();
    }, []);

    const submit = useCallback(async() => {
        const response = await doRequest.apiProfile<number>(
            confirmEmail({
                key: code,
                contact_id: contactId,
            }),
        );

        if (response.isSuccess) {
            await emailConfirmedHandler();
        } else {
            setCodeError({
                status: true,
                message: response.error.message ? response.error.message : 'Код указан неверно, попробуйте ввести его еще раз',
            });
        }
    }, [code, contactId, emailConfirmedHandler]);

    switch (popupStep) {
        case (EPopupSteps.success):
            return (<PopupResult
                isOpen={showPopup}
                onClose={closePopupHandler}
                title='Готово'
                type={EResultPopupStatus.SUCCESS}
            >
                <ST.Text>Теперь уведомления будут приходить вам на почту.
                    <Link href='/lk/user/profile#notices'><a> Настройте уведомления</a></Link>
                </ST.Text>
                <ST.Button type={btnTypes.SECONDARY} onClick={closePopupHandler}>Хорошо</ST.Button>
            </PopupResult>);
        case (EPopupSteps.error):
            return (<PopupResult
                isOpen={showPopup}
                onClose={closePopupHandler}
                title='Произошла ошибка'
                text={errorMessage}
                type={EResultPopupStatus.ERROR}
            >
                <ST.Text>
                    Попробуйте позже или напишите в{NON_BREAKING_SPACE}<OpenChat/>
                </ST.Text>
            </PopupResult>);
        case (EPopupSteps.addedUnconfirmedEmail):
            return (<ST.EmailPopup isOpen={showPopup} onClose={closePopupHandler} data-test='popupAddEmail'>
                <ST.Header>Мы выслали вам на почту код подтверждения.<br/>Пожалуйста, введите его в поле ниже</ST.Header>
                <ST.Body>
                    <ST.EmailBlock>
                        <ST.EmailInput
                            type='email'
                            value={email}
                            disabled
                            data-test='popupEmailInput'
                        />
                        <ST.EmailNotice>
                            На вашу электронную почту отправлено письмо с подтверждением.<br/>
                            Перейдите по ссылке в письме или введите код подтверждения.
                        </ST.EmailNotice>
                    </ST.EmailBlock>
                    <ST.CodeBlock>
                        <ST.CodeInput
                            onChange={changeCode}
                            required={true}
                            errorMessage={codeError}
                            placeholder='Код из письма'
                            data-test='popupCodeInput'
                        />
                        <ST.ErrorMessage hidden={!codeError.status}>{codeError.message}</ST.ErrorMessage>
                        <ST.SendAgainLink onClick={sendCodeAgain}>Отправить письмо повторно</ST.SendAgainLink>
                    </ST.CodeBlock>
                    <ST.Button
                        type={btnTypes.SECONDARY}
                        onClick={submit}
                        disabled={!code}
                        data-test='popupSubmitBtn'
                    >
                        Отправить
                    </ST.Button>
                </ST.Body>
            </ST.EmailPopup>);
        default:
            return <></>;
    }
};
